/**
 * Order creation modal component.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/apiClient';
import { useAuth } from '@/contexts/AuthContext';
import toast from 'react-hot-toast';
import { formatDate } from '@/utils/helpers';

interface OrderModalProps {
  show: boolean;
  onHide: () => void;
  datasetId: number;
}

interface Dataset {
  id: number;
  device_id_str?: string;
  data_type_name?: string;
  start_time?: string;
  end_time?: string;
}

interface DatasetsResponse {
  items?: Dataset[];
}

export default function OrderModal({ show, onHide, datasetId }: OrderModalProps) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(user?.phone_number || '');
  const [tosAccepted, setTosAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch dataset details
  const { data: datasetsData } = useQuery<DatasetsResponse>({
    queryKey: ['datasets'],
    queryFn: async () => {
      const response = await apiClient.getDatasets(1, 100);
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data as DatasetsResponse;
    },
  });

  const selectedDataset = datasetsData?.items?.find((d) => d.id === datasetId);

  useEffect(() => {
    if (selectedDataset) {
      // Set default times from dataset
      if (selectedDataset.start_time) {
        const start = new Date(selectedDataset.start_time);
        setStartTime(start.toISOString().slice(0, 16));
      }
      if (selectedDataset.end_time) {
        const end = new Date(selectedDataset.end_time);
        setEndTime(end.toISOString().slice(0, 16));
      }
    }
  }, [selectedDataset]);

  const handleSubmit = async () => {
    if (!tosAccepted) {
      toast.error(t('modals.order.acceptTos'));
      return;
    }
    if (!phoneNumber) {
      toast.error(t('modals.order.enterPhone'));
      return;
    }
    if (!startTime || !endTime) {
      toast.error(t('modals.order.selectTimes'));
      return;
    }

    setIsSubmitting(true);
    try {
      const datasetTimes = {
        [datasetId]: {
          start_time: new Date(startTime).toISOString(),
          end_time: new Date(endTime).toISOString(),
        },
      };

      const response = await apiClient.createOrder([datasetId], datasetTimes, phoneNumber, tosAccepted);

      if (response.error) {
        toast.error(response.error);
        return;
      }

      toast.success(t('modals.order.created'));
      handleClose();
    } catch (error) {
      toast.error(t('modals.order.failed'));
      console.error('Order creation error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setStartTime('');
    setEndTime('');
    setTosAccepted(false);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>{t('modals.order.title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {selectedDataset && (
          <div className="mb-3">
            <h5>{t('modals.order.selectedDataset')}</h5>
            <p><strong>{t('modals.order.deviceId')}:</strong> {selectedDataset.device_id_str}</p>
            <p><strong>{t('modals.order.dataType')}:</strong> {selectedDataset.data_type_name}</p>
            <p><strong>{t('modals.order.timeRange')}:</strong> {formatDate(selectedDataset.start_time)} - {formatDate(selectedDataset.end_time)}</p>
          </div>
        )}
        <div className="mb-3">
          <h5>{t('modals.order.timeRange')}</h5>
          <Form.Group className="mb-3">
            <Form.Label>{t('modals.order.startTime')}</Form.Label>
            <Form.Control
              type="datetime-local"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('modals.order.endTime')}</Form.Label>
            <Form.Control
              type="datetime-local"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </Form.Group>
        </div>
        <div className="mb-3">
          {!user?.phone_verified && (
            <Alert variant="warning" className="mb-3">
              {t('profile.notVerified')}
            </Alert>
          )}
          <Form.Group className="mb-3">
            <Form.Label>{t('modals.order.phoneNumber')}</Form.Label>
            <Form.Control
              type="tel"
              placeholder={t('modals.order.phonePlaceholder')}
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
            />
          </Form.Group>
          <Form.Check
            type="checkbox"
            label={t('modals.order.tosAccepted')}
            checked={tosAccepted}
            onChange={(e) => setTosAccepted(e.target.checked)}
            className="mb-3"
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          {t('common.close')}
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
          {t('modals.order.placeOrder')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
